<script lang="ts" setup>
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { getDateRangeFormatted } from '@/services/date'
import { mdiDotsHorizontal } from '@mdi/js'
import { ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import BKSvgIcon from '@/components/AppShared/BKSvgIcon.vue'
import { EventPreview } from '@/enitites/event/model/types'
import { useRouter } from 'vue-router/composables'
import IconLike from '@/shared/icons/IconLike.vue'
import ToggleLike from '../../../features/toggle-like/ui/ToggleLike.vue'

const { getEventImg } = useImage()

const props = defineProps<{
  event: EventPreview
}>()

const router = useRouter()

const isShowDeleteModal = ref(false)

const getEventDate = getDateRangeFormatted(props.event.dateStart, props.event.dateEnd)

const handleClickEvent = () => {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
  })
}

const handleProvideFeedback = () => {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
    query: { tab: 'feedback' },
  })
}

const handleReviewFeedback = () => {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
    query: { tab: 'feedback' },
  })
}
</script>

<template>
  <v-col cols="12">
    <button
      class="event-row align-start"
      @click="handleClickEvent"
    >
      <v-img
        :src="getEventImg(event.image, 800)"
        class="flex-grow-0 mr-4 event-img"
      />
      <div class="mr-auto col-6 py-0">
        <p class="event-title">
          {{ getEventDate }}
        </p>
        <p class="events__title">
          <span v-if="event.status === 'draft'"> <span class="draft-label">Draft</span> - </span>
          <span>
            {{ event.title || 'Event name' }}
          </span>
        </p>
      </div>

      <div v-if="event.isFinished">
        <div
          v-if="event.canReviewFeedback"
          class="feedback-wrapper"
        >
          <p class="likes-count">Likes: {{ event.countLikes }} <IconLike color="currentColor" /></p>
          <button @click.stop="handleReviewFeedback">
            Review feedback

            <BKSvgIcon
              class="arrow-icon"
              color="#A38379"
              height="24px"
              name="arrow-with-stem"
              width="24px"
            />
          </button>
        </div>
        <template v-else>
          <div
            v-if="event.canLeaveFeedback || event.canLike"
            class="feedback-wrapper"
          >
            <p>Did you enjoy this event?</p>
            <template>
              <ToggleLike
                v-if="event.canLike"
                :event-id="event.id"
                :is-liked="event.isLiked"
                color-svg="#A38379"
                @update:is-liked="(newValue) => (event.isLiked = newValue)"
              />
            </template>
            <button
              v-if="event.canLeaveFeedback"
              @click.stop="handleProvideFeedback"
            >
              Provide feedback

              <BKSvgIcon
                class="arrow-icon"
                color="#A38379"
                height="24px"
                name="arrow-with-stem"
                width="24px"
              />
            </button>
          </div>
          <div v-else>
            <p class="count-likes">Likes <IconLike color="#A38379" /> {{ event.countLikes }}</p>
          </div>
        </template>
      </div>

      <div
        class="d-flex align-center"
        @click.stop=""
      >
        <!-- Event options dropdown -->
        <v-menu
          v-if="event.status === 'draft'"
          bottom
          left
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="mx-5"
              icon
              v-on="on"
            >
              <v-icon>
                {{ mdiDotsHorizontal }}
              </v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="isShowDeleteModal = true"> Delete Event</v-list-item>
            <v-list-item
              :to="{ name: 'CreateEventProcessing', params: { id: event.id } }"
              link
            >
              Edit Event
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </button>

    <div class="line" />
  </v-col>
</template>

<style lang="scss" scoped>
@import '~@/assets/style/mixins';

.event-row {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  transition: all 0.3s ease;

  .event-link {
    color: inherit;

    &::after {
      position: absolute;
      inset: 0;
      content: '';
    }
  }
}

.count-likes {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #a38379;
}

.draft-label {
  color: #ff2d53;
}

.event-title {
  @include font-caption1;

  margin: 0 4px 0 0;
  color: var(--color-grey-300);
  font-style: normal;

  @media (min-width: 600px) {
    @include font-body1;
  }
}

.line {
  border-bottom: 1px solid #ddd;
}

.event-img {
  width: 80px;
  height: 80px;

  @media (min-width: 600px) {
    width: 200px;
    height: 100px;
  }

  &:hover {
    opacity: 0.7;
  }
}

.feedback-wrapper {
  flex: 0 1 330px;
  align-self: stretch;
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  color: #a38379;
  font-size: 16px;
  font-weight: 400;
  flex-shrink: 0;

  button {
    display: flex;
    align-items: center;
    gap: 8px;

    .arrow-icon {
      transform: rotate(180deg);
    }
  }

  p {
    margin: 0;
  }

  .likes-count {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
</style>
